import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Plus, Sparkles, CheckCircle2, Send, Download, Loader2,
  ExternalLink, X, Trash2, DollarSign, Receipt,
} from "lucide-react";
import {
  estimatesList, estimateGet, estimateCreate, estimatePatch,
  estimateAiDraft, estimateApprove, estimateSend,
  estimateConvertToInvoice, estimatePdfDownload,
  invoicesList, invoiceGet, invoicePdfDownload, invoicePaymentLink,
  invoiceMarkPaid, invoiceSend,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * EstimatesInvoicesPage — /estimates
 *
 * Simple three-step flow so any business owner can build and send a quote,
 * turn it into an invoice, and collect payment — without leaving the app.
 *   Step 1: Draft (add lines by hand or let AI draft them from the inbox
 *   thread — every line is editable).
 *   Step 2: Approve — required before anything leaves the building.
 *   Step 3: Send → Convert to invoice → Get a payment link (Stripe by
 *   default, Razorpay for INR).
 */

const STATUS_TONE = {
  draft: "border-slate-300 bg-slate-100 text-slate-700",
  awaiting_approval: "border-amber-300 bg-amber-50 text-amber-800",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-800",
  sent: "border-sky-300 bg-sky-50 text-sky-800",
  accepted: "border-emerald-400 bg-emerald-100 text-emerald-900",
  declined: "border-rose-300 bg-rose-50 text-rose-700",
  converted: "border-violet-300 bg-violet-50 text-violet-800",
  expired: "border-slate-300 bg-slate-50 text-slate-500",
  paid: "border-emerald-400 bg-emerald-100 text-emerald-900",
  void: "border-slate-300 bg-slate-50 text-slate-500",
};

export default function EstimatesInvoicesPage() {
  const location = useLocation();
  // /invoices → land on the Invoices tab so the URL matches the view.
  const initialTab = (location.pathname || "").startsWith("/invoices")
    ? "invoices" : "estimates";
  const [tab, setTab] = useState(initialTab);
  const [estimates, setEstimates] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [open, setOpen] = useState(null);  // full estimate/invoice detail
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [e, i] = await Promise.all([
      estimatesList().catch(() => null),
      invoicesList().catch(() => null),
    ]);
    setEstimates(e); setInvoices(i);
  };
  useEffect(() => { document.title = "Estimates & invoices — WebsiteTech.pro"; load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="estimates-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <FileText className="h-4 w-4" /> Estimates & invoices
          </div>
          <h1 className="mt-1 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Quote → approve → invoice → get paid
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Draft a quote by hand or let AI turn any inbox thread into line
            items. Approve it (nothing sends without your sign-off), then
            convert it to an invoice and share a payment link — Stripe by
            default, Razorpay if the invoice is in INR.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={tab === "estimates" ? "default" : "outline"}
              className={tab === "estimates" ? "bg-slate-900 text-white" : ""}
              onClick={() => setTab("estimates")}
              data-testid="tab-estimates"
            >
              <FileText className="mr-1 h-4 w-4" /> Estimates
              <Badge variant="outline" className="ml-2">
                {estimates?.estimates?.length ?? "…"}
              </Badge>
            </Button>
            <Button
              size="sm"
              variant={tab === "invoices" ? "default" : "outline"}
              className={tab === "invoices" ? "bg-slate-900 text-white" : ""}
              onClick={() => setTab("invoices")}
              data-testid="tab-invoices"
            >
              <Receipt className="mr-1 h-4 w-4" /> Invoices
              <Badge variant="outline" className="ml-2">
                {invoices?.invoices?.length ?? "…"}
              </Badge>
            </Button>
            <Button
              size="sm"
              className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={() => setShowNew(true)}
              data-testid="new-estimate-btn"
            >
              <Plus className="mr-1 h-4 w-4" /> New estimate
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-6">
        {tab === "estimates" ? (
          <EstimateList data={estimates} onOpen={async (id) => {
            const d = await estimateGet(id);
            setOpen({ kind: "estimate", data: d.estimate });
          }} />
        ) : (
          <InvoiceList data={invoices} onOpen={async (id) => {
            const d = await invoiceGet(id);
            setOpen({ kind: "invoice", data: d.invoice });
          }} />
        )}
      </section>

      {open && (
        <DocumentModal
          doc={open}
          onClose={() => setOpen(null)}
          onRefresh={async (id) => {
            if (open.kind === "estimate") {
              const d = await estimateGet(id);
              setOpen({ kind: "estimate", data: d.estimate });
            } else {
              const d = await invoiceGet(id);
              setOpen({ kind: "invoice", data: d.invoice });
            }
            await load();
          }}
          onOpenInvoice={async (id) => {
            const d = await invoiceGet(id);
            setOpen({ kind: "invoice", data: d.invoice });
            setTab("invoices");
            await load();
          }}
        />
      )}

      {showNew && (
        <NewEstimateModal
          onClose={() => setShowNew(false)}
          onCreated={async (est) => {
            setShowNew(false);
            await load();
            const d = await estimateGet(est.id);
            setOpen({ kind: "estimate", data: d.estimate });
          }}
        />
      )}

      <Footer />
    </div>
  );
}

function EstimateList({ data, onOpen }) {
  if (!data) return <Loader />;
  if (!data.estimates?.length) return <Empty text="No estimates yet — start with New estimate." />;
  return (
    <Card className="overflow-hidden border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="estimates-table">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Number</th>
            <th className="px-3 py-2">Title</th>
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-right">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.estimates.map((e) => (
            <tr key={e.id} onClick={() => onOpen(e.id)}
              className="cursor-pointer hover:bg-slate-50"
              data-testid={`estimate-row-${e.id}`}>
              <td className="px-3 py-2 font-mono text-xs">{e.number}</td>
              <td className="px-3 py-2 font-medium text-slate-900">{e.title}</td>
              <td className="px-3 py-2 text-slate-600">
                {e.customer_name || e.customer_email || <span className="text-slate-400">—</span>}
              </td>
              <td className="px-3 py-2">
                <Badge variant="outline" className={STATUS_TONE[e.status]}>{e.status}</Badge>
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {e.currency} {(e.totals?.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
              <td className="px-3 py-2 text-right text-xs text-slate-500">
                {new Date(e.updated_at || e.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function InvoiceList({ data, onOpen }) {
  if (!data) return <Loader />;
  if (!data.invoices?.length) return <Empty text="No invoices yet — convert an approved estimate to create one." />;
  return (
    <Card className="overflow-hidden border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="invoices-table">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Number</th>
            <th className="px-3 py-2">Title</th>
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-right">Due</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.invoices.map((i) => (
            <tr key={i.id} onClick={() => onOpen(i.id)}
              className="cursor-pointer hover:bg-slate-50"
              data-testid={`invoice-row-${i.id}`}>
              <td className="px-3 py-2 font-mono text-xs">{i.number}</td>
              <td className="px-3 py-2 font-medium text-slate-900">{i.title}</td>
              <td className="px-3 py-2 text-slate-600">
                {i.customer_name || i.customer_email || <span className="text-slate-400">—</span>}
              </td>
              <td className="px-3 py-2">
                <Badge variant="outline" className={STATUS_TONE[i.status]}>{i.status}</Badge>
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {i.currency} {(i.totals?.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
              <td className="px-3 py-2 text-right text-xs text-slate-500">
                {i.due_at ? new Date(i.due_at).toLocaleDateString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function DocumentModal({ doc, onClose, onRefresh, onOpenInvoice }) {
  const isEstimate = doc.kind === "estimate";
  const d = doc.data;
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState(d.lines || []);
  const [notes, setNotes] = useState(d.notes || "");
  const [tax, setTax] = useState(d.tax_pct || 0);
  const editable = isEstimate && (d.status === "draft" || d.status === "awaiting_approval");

  // Track edits so we can auto-save before an approve/convert action
  const dirty = editable && (
    JSON.stringify(lines) !== JSON.stringify(d.lines || [])
    || notes !== (d.notes || "")
    || Number(tax) !== Number(d.tax_pct || 0)
  );

  const totals = (() => {
    const subtotal = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
    const taxAmt = subtotal * (Number(tax || 0) / 100);
    return { subtotal, tax: taxAmt, total: subtotal + taxAmt };
  })();

  const save = async (silent = false) => {
    setBusy(true);
    try {
      await estimatePatch(d.id, { lines, notes, tax_pct: Number(tax) });
      if (!silent) toast.success("Saved");
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    } finally { setBusy(false); }
  };

  const doAiDraft = async () => {
    setBusy(true);
    try {
      const r = await estimateAiDraft(d.id, {
        thread_id: d.thread_id || null,
        brief: d.title,
      });
      toast.success(`AI drafted ${r.estimate.lines.length} lines`);
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setBusy(false); }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      // Auto-save any pending edits so the customer never sees the
      // confusing "add at least one line item first" toast when they
      // just typed lines but forgot to click Save.
      if (dirty) {
        await estimatePatch(d.id, { lines, notes, tax_pct: Number(tax) });
      }
      await estimateApprove(d.id);
      toast.success("Approved — you can now send it or convert to invoice");
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const r = await estimateSend(d.id);
      if (r.email_sent) {
        toast.success(`Emailed to ${r.recipient}`);
      } else {
        toast.warning(r.detail || "Marked as sent (email not configured)");
      }
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const doConvert = async () => {
    setBusy(true);
    try {
      const r = await estimateConvertToInvoice(d.id, 14);
      toast.success(`Invoice ${r.invoice.number} created`);
      // Immediately jump into the new invoice so the payment-link CTA is
      // one click away.
      if (onOpenInvoice) {
        await onOpenInvoice(r.invoice.id);
      } else {
        await onRefresh(d.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setBusy(false); }
  };

  const doPayLink = async () => {
    setBusy(true);
    try {
      const r = await invoicePaymentLink(d.id);
      toast.success(`Payment link via ${r.provider}`);
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment link failed");
    } finally { setBusy(false); }
  };

  const doMarkPaid = async () => {
    setBusy(true);
    try {
      await invoiceMarkPaid(d.id);
      toast.success("Marked as paid");
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark paid failed");
    } finally { setBusy(false); }
  };

  const doInvoiceSend = async () => {
    setBusy(true);
    try {
      const r = await invoiceSend(d.id);
      if (r.email_sent) {
        toast.success("Invoice emailed to customer");
      } else {
        toast.error(r.detail || "Couldn't send email");
      }
      await onRefresh(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose} data-testid="doc-modal">
      <Card className="max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-slate-200 bg-white p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {isEstimate ? "Estimate" : "Invoice"} · {d.number}
            </div>
            <h2 className="mt-1 font-heading text-2xl font-bold text-slate-900">{d.title}</h2>
            <div className="mt-1 text-sm text-slate-600">
              {d.customer_name || d.customer_email || <span className="italic text-slate-400">No customer</span>}
              {d.customer_email && d.customer_name ? ` · ${d.customer_email}` : ""}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge variant="outline" className={STATUS_TONE[d.status]}>{d.status}</Badge>
              {d.thread_id && <Badge variant="outline">from inbox</Badge>}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Line items */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Line items</div>
            {editable && (
              <Button size="sm" variant="outline" onClick={doAiDraft} disabled={busy}
                data-testid="ai-draft-btn">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
                AI draft from inbox
              </Button>
            )}
          </div>
          <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2 w-16 text-right">Qty</th>
                  <th className="px-2 py-2 w-28 text-right">Unit price</th>
                  <th className="px-2 py-2 w-28 text-right">Amount</th>
                  {editable && <th className="w-8"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.map((ln, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1">
                      {editable ? (
                        <input value={ln.description}
                          onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                          className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 hover:border-slate-200 focus:border-slate-300 focus:bg-white" />
                      ) : ln.description}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {editable ? (
                        <input type="number" step="0.5" value={ln.qty}
                          onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))}
                          className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-right hover:border-slate-200 focus:border-slate-300 focus:bg-white" />
                      ) : ln.qty}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {editable ? (
                        <input type="number" step="0.01" value={ln.unit_price}
                          onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x))}
                          className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-right hover:border-slate-200 focus:border-slate-300 focus:bg-white" />
                      ) : ln.unit_price.toFixed(2)}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {(Number(ln.qty || 0) * Number(ln.unit_price || 0)).toFixed(2)}
                    </td>
                    {editable && (
                      <td className="px-1">
                        <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
                          className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editable && (
            <Button size="sm" variant="outline" className="mt-2"
              onClick={() => setLines([...lines, { description: "", qty: 1, unit_price: 0 }])}
              data-testid="add-line-btn">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add line
            </Button>
          )}
        </div>

        {/* Totals + tax */}
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notes for the customer</div>
            <textarea rows={3}
              value={notes}
              disabled={!editable}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-50" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Totals</div>
            <div className="mt-1 rounded-md border border-slate-200 p-3 text-sm">
              <Row k="Subtotal" v={`${d.currency} ${totals.subtotal.toFixed(2)}`} />
              <div className="flex items-center justify-between">
                <label className="text-slate-600">Tax %</label>
                {editable ? (
                  <input type="number" value={tax}
                    onChange={(e) => setTax(e.target.value)}
                    className="w-16 rounded border border-slate-300 px-1 py-0.5 text-right text-sm" />
                ) : <span className="font-mono">{Number(d.tax_pct || 0)}</span>}
              </div>
              <Row k="Tax amount" v={`${d.currency} ${totals.tax.toFixed(2)}`} />
              <div className="mt-1 border-t border-slate-200 pt-1">
                <Row k="Total" v={`${d.currency} ${totals.total.toFixed(2)}`} bold />
              </div>
            </div>
          </div>
        </div>

        {d.ai_assumptions?.length ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="font-semibold">Assumptions we made — double-check before sending:</div>
            <ul className="ml-4 mt-1 list-disc">
              {d.ai_assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        ) : null}

        {/* Payment link — only on invoices */}
        {!isEstimate && d.payment_link_url && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <b>Payment link ({d.payment_link_provider}):</b>{" "}
            <a href={d.payment_link_url} target="_blank" rel="noreferrer"
              className="break-all text-emerald-700 underline">
              {d.payment_link_url}
            </a>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline"
            onClick={() => isEstimate
              ? estimatePdfDownload(d.id, d.number)
              : invoicePdfDownload(d.id, d.number)}
            data-testid="download-pdf-btn">
            <Download className="mr-1 h-4 w-4" /> Download PDF
          </Button>
          {isEstimate && editable && (
            <>
              <Button variant="outline" onClick={save} disabled={busy}
                data-testid="save-estimate-btn">Save</Button>
              <Button className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={doApprove} disabled={busy || !lines.length}
                data-testid="approve-btn">
                <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
              </Button>
            </>
          )}
          {isEstimate && d.status === "approved" && (
            <>
              <Button variant="outline" onClick={doSend} disabled={busy}
                data-testid="send-estimate-btn">
                <Send className="mr-1 h-4 w-4" /> Mark as sent
              </Button>
              <Button className="bg-orange-600 text-white hover:bg-orange-700"
                onClick={doConvert} disabled={busy}
                data-testid="convert-btn">
                <Receipt className="mr-1 h-4 w-4" /> Convert to invoice
              </Button>
            </>
          )}
          {isEstimate && d.status === "sent" && (
            <Button className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={doConvert} disabled={busy}
              data-testid="convert-btn">
              <Receipt className="mr-1 h-4 w-4" /> Convert to invoice
            </Button>
          )}
          {!isEstimate && d.status !== "paid" && !d.payment_link_url && (
            <Button className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={doPayLink} disabled={busy}
              data-testid="payment-link-btn">
              <DollarSign className="mr-1 h-4 w-4" /> Create payment link
            </Button>
          )}
          {!isEstimate && d.status !== "paid" && d.customer_email && (
            <Button variant="outline" onClick={doInvoiceSend} disabled={busy}
              data-testid="email-invoice-btn">
              <Send className="mr-1 h-4 w-4" /> Email invoice
            </Button>
          )}
          {!isEstimate && d.status !== "paid" && (
            <Button variant="outline" onClick={doMarkPaid} disabled={busy}
              data-testid="mark-paid-btn">
              <CheckCircle2 className="mr-1 h-4 w-4" /> Mark paid
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function NewEstimateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Give it a title"); return; }
    setBusy(true);
    try {
      const r = await estimateCreate({
        title: title.trim(), customer_email: email || null,
        customer_name: name || null, currency, lines: [], tax_pct: 0,
      });
      onCreated(r.estimate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose} data-testid="new-estimate-modal">
      <Card className="w-full max-w-md border border-slate-200 bg-white p-5"
        onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-slate-900">New estimate</h3>
        <p className="mt-1 text-xs text-slate-500">
          Start empty or use AI to draft lines from an inbox thread on the next screen.
        </p>
        <div className="mt-3 space-y-3">
          <FieldSimple label="Title" v={title} onChange={setTitle}
            placeholder="e.g. Kitchen remodel — Smith residence"
            testid="new-title" />
          <FieldSimple label="Customer name" v={name} onChange={setName}
            testid="new-cust-name" />
          <FieldSimple label="Customer email" v={email} onChange={setEmail}
            testid="new-cust-email" />
          <div>
            <label className="text-xs font-semibold text-slate-500">Currency</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              data-testid="new-currency">
              {["USD", "EUR", "GBP", "INR", "CAD", "AUD"].map((c) =>
                <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-orange-600 text-white hover:bg-orange-700"
            onClick={submit} disabled={busy}
            data-testid="new-estimate-submit">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Create draft
          </Button>
        </div>
      </Card>
    </div>
  );
}

function FieldSimple({ label, v, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <input type="text" value={v} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        data-testid={testid} />
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold text-slate-900" : "text-slate-600"}`}>
      <span>{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center gap-2 text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}

function Empty({ text }) {
  return (
    <Card className="border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
      {text}
    </Card>
  );
}
